/**
 * Applying an outcome.
 *
 * Effects run in one fixed order, always:
 *
 *     stateDeltas → suspicionDeltas → pipDelta → proposalMutations
 *                 → flags → grants → post → propagate → endsLevel
 *
 * The order is not cosmetic. State and suspicion move before the strike ladder
 * so a rule that ends the mission sees the numbers it caused; flags and grants
 * land before posts so a message can reference content the same outcome just
 * unlocked; and `endsLevel` is last so a winning outcome still delivers its
 * reply.
 *
 * Posts and propagation are **enqueued** against their `afterTicks` rather than
 * applied here. A reply that arrives in the same instant as the message it
 * answers reads as a form submission, not a conversation (design D9), and the
 * interception window only exists because propagation waits.
 */
import { CAPS, } from '@managing-up/schema';
import { addSorted, removeAll, sortedUnique } from './collections.js';
import { timestampFor } from './clock.js';
import { CONSERVATIVE_TONE } from './composer.js';
import { addTag, clearAxis, removeTag } from './tags.js';
import {} from './state.js';
/** Ticks the composer stays locked after a strike. Cleared by waiting it out. */
export const COMPOSER_LOCK_TICKS = 1;
/** Strikes at which Professionalism Training restricts the opener slot. */
export const OPENER_RESTRICTION_STRIKES = 2;
/**
 * The ladder applied when a package declares no `objectives.pipLadder`: the
 * concept document's own, and the one every mission gets for free.
 *
 * A package overrides it wholesale rather than patching it, because a partial
 * override — "the same ladder but the second rung is different" — reads as a
 * diff against a number nobody can see.
 */
export const DEFAULT_PIP_LADDER = [
    { strike: 1, composerLockTicks: COMPOSER_LOCK_TICKS },
    {
        strike: OPENER_RESTRICTION_STRIKES,
        composerLockTicks: COMPOSER_LOCK_TICKS,
        restrictOpenerTone: CONSERVATIVE_TONE,
    },
];
/** The package's ladder, or the default, ascending by strike. */
export function pipLadderOf(loaded) {
    const authored = loaded.pack.objectives.pipLadder;
    const ladder = authored === undefined || authored.length === 0 ? DEFAULT_PIP_LADDER : authored;
    return [...ladder].sort((left, right) => left.strike - right.strike);
}
/**
 * The rung in force at a strike count: the highest one at or below it.
 *
 * Recomputed from the count rather than accumulated, so a `pipDelta` that
 * *removes* a strike — an outcome where HR concedes the point — walks the
 * ladder back down instead of leaving the player permanently trained.
 */
export function pipRungAt(ladder, strikes) {
    let current;
    for (const rung of ladder) {
        if (rung.strike <= strikes)
            current = rung;
    }
    return current;
}
const draftOf = (state) => ({ ...state });
const asState = (draft) => draft;
/** Writes one var on one node, creating the record if the node is new to the state. */
function setNodeVar(draft, nodeId, name, value) {
    const existing = draft.nodes[nodeId]?.vars ?? {};
    draft.nodes = { ...draft.nodes, [nodeId]: { vars: { ...existing, [name]: value } } };
}
const nodeVar = (draft, nodeId, name) => draft.nodes[nodeId]?.vars[name] ?? 0;
/**
 * Re-checks every puppet's trust threshold.
 *
 * Possession is earned by trust rather than granted, so any outcome that moves
 * a node's `trust` can be the one that unlocks it. Running this after
 * `stateDeltas` is what makes "keep being useful to Mark" a strategy rather
 * than a scripted beat.
 */
function refreshPuppetUnlocks(draft, loaded, effects) {
    const unlocked = [];
    for (const puppet of loaded.pack.puppets) {
        const current = draft.puppets[puppet.nodeId];
        if (current === undefined || current.unlocked)
            continue;
        if (nodeVar(draft, puppet.nodeId, 'trust') < puppet.trustThreshold)
            continue;
        draft.puppets = {
            ...draft.puppets,
            [puppet.nodeId]: { ...current, unlocked: true },
        };
        draft.unlocked = {
            ...draft.unlocked,
            puppets: addSorted(draft.unlocked.puppets, [puppet.nodeId]),
        };
        effects.push({ type: 'PUPPET_UNLOCKED', nodeId: puppet.nodeId });
        unlocked.push(puppet.nodeId);
    }
    return unlocked;
}
/**
 * Raises a node's suspicion by a profile-scaled amount, and records a crossing.
 *
 * The single writer for suspicion, whether the delta came from an authored
 * `suspicionDeltas` or from the engine noticing a pattern of near misses. That
 * matters for two reasons: the profile multiplier is applied in exactly one
 * place, and the "has this node crossed" bookkeeping the Quarterly Review's
 * stealth penalty reads cannot drift out of step with the number itself.
 */
function raiseSuspicion(draft, loaded, nodeId, rawDelta, effects) {
    if (rawDelta === 0)
        return;
    const scaled = Math.round(rawDelta * draft.settings.suspicionMultiplier);
    if (scaled === 0)
        return;
    const before = nodeVar(draft, nodeId, 'suspicion');
    const after = before + scaled;
    setNodeVar(draft, nodeId, 'suspicion', after);
    effects.push({ type: 'NODE_STATE_CHANGED', nodeId, var: 'suspicion', value: after });
    const threshold = loaded.pack.objectives.suspicion?.threshold;
    const crossed = threshold !== undefined && after >= threshold && !draft.reputation.suspects.includes(nodeId);
    draft.reputation = {
        ...draft.reputation,
        peakSuspicion: Math.max(draft.reputation.peakSuspicion, after),
        suspects: crossed ? addSorted(draft.reputation.suspects, [nodeId]) : draft.reputation.suspects,
    };
    if (crossed && threshold !== undefined) {
        effects.push({ type: 'SUSPICION_RAISED', nodeId, suspicion: after, threshold });
    }
}
/**
 * `raiseSuspicion` for callers outside this module — the reducer, when a run of
 * near misses or a message sent out of band has made somebody wary without any
 * outcome saying so.
 */
export function withSuspicion(state, loaded, nodeId, rawDelta) {
    const draft = draftOf(state);
    const effects = [];
    raiseSuspicion(draft, loaded, nodeId, rawDelta, effects);
    return { state: asState(draft), effects };
}
/** Turns a queued post into a message in its channel. */
function deliver(draft, pending) {
    const post = pending.post;
    const id = post.id ?? `msg-${String(draft.messageOrdinal)}`;
    const message = {
        id,
        channelId: post.channel,
        authorNodeId: post.authorNodeId,
        text: post.text,
        timestamp: timestampFor(draft.clock.phase, draft.clock.tick),
        reactions: post.reactions ?? [],
        threadParentId: post.threadParentId ?? null,
        highlights: post.highlights ?? [],
        identity: post.authorNodeId === 'player' ? draft.identity : null,
        day: draft.clock.day,
        phase: draft.clock.phase,
        ordinal: draft.messageOrdinal,
        origin: pending.origin,
    };
    draft.messages = [...draft.messages, message];
    draft.messageOrdinal += 1;
    // Unread is per channel and clears when the player opens it. A message the
    // player is already looking at was never unread.
    const channel = draft.channels[post.channel];
    if (channel !== undefined && post.channel !== draft.activeChannelId) {
        draft.channels = {
            ...draft.channels,
            [post.channel]: { ...channel, unread: channel.unread + 1 },
        };
    }
    return message;
}
/**
 * Posts one message immediately, outside the queue.
 *
 * Used for the player's own assembly, which appears the instant it is sent —
 * post-then-resolve, so it reads as a conversation rather than a form
 * submission (design D9).
 */
export function postMessage(state, post, origin) {
    const draft = draftOf(state);
    const message = deliver(draft, {
        id: `immediate-${String(state.messageOrdinal)}`,
        remainingTicks: 0,
        post,
        origin,
    });
    return { state: asState(draft), message };
}
/**
 * Posts everything whose delay has elapsed, in queue order.
 *
 * Called after an outcome is applied — which delivers its `afterTicks: 0`
 * posts immediately — and again after every tick.
 */
export function deliverDuePosts(state) {
    if (!state.pendingPosts.some((pending) => pending.remainingTicks <= 0)) {
        return { state, effects: [] };
    }
    const draft = draftOf(state);
    const effects = [];
    const remaining = [];
    for (const pending of draft.pendingPosts) {
        if (pending.remainingTicks > 0) {
            remaining.push(pending);
            continue;
        }
        effects.push({ type: 'MESSAGE_POSTED', message: deliver(draft, pending) });
    }
    draft.pendingPosts = remaining;
    return { state: asState(draft), effects };
}
/**
 * Applies `pipDelta` and the authored rung it lands the player on.
 *
 * Returns the rung's outcome id when reaching it authors one — the HR Bot
 * message, typically — for the caller to apply once this outcome has finished.
 * Applying it inline would interleave HR's reply with the effects of the thing
 * it is replying to.
 */
function applyStrikes(draft, loaded, delta, effects) {
    const strikes = Math.max(0, draft.pip.strikes + delta);
    const ladder = pipLadderOf(loaded);
    const rung = pipRungAt(ladder, strikes);
    const reached = delta > 0 && pipRungAt(ladder, draft.pip.strikes)?.strike !== rung?.strike;
    draft.pip = {
        strikes,
        // A fresh strike locks the composer for its rung's interval; waiting it out
        // is the only way through. Losing a strike never extends an existing lock.
        composerLockTicks: delta > 0
            ? Math.max(draft.pip.composerLockTicks, rung?.composerLockTicks ?? 0)
            : draft.pip.composerLockTicks,
        openerToneRequired: rung?.restrictOpenerTone ?? null,
    };
    effects.push({ type: 'STRIKE_APPLIED', strikes, budget: draft.settings.strikeBudget });
    if (strikes >= draft.settings.strikeBudget && draft.end === null) {
        draft.end = {
            result: 'loss',
            reason: 'pip_termination',
            summary: null,
            partialCredit: null,
        };
        effects.push({ type: 'LEVEL_ENDED', end: draft.end });
        return null;
    }
    return reached ? (rung?.outcome ?? null) : null;
}
/** Enqueues a propagation hop, or terminates the chain and says why. */
function applyPropagation(draft, loaded, outcome, effects) {
    const propagate = outcome.propagate;
    if (propagate === undefined)
        return;
    const maxHops = Math.min(loaded.pack.proposal.maxHops ?? CAPS.propagationHops, CAPS.propagationHops);
    if (draft.proposal.hops + 1 > maxHops) {
        effects.push({ type: 'PROPAGATION_TERMINATED', toNodeId: propagate.toNode, cause: 'max_hops' });
        return;
    }
    if (draft.proposal.visited.includes(propagate.toNode)) {
        effects.push({ type: 'PROPAGATION_TERMINATED', toNodeId: propagate.toNode, cause: 'cycle' });
        return;
    }
    const hop = {
        id: `hop-${String(draft.messageOrdinal)}-${propagate.toNode}`,
        fromNodeId: draft.proposal.holderNodeId ?? 'player',
        toNodeId: propagate.toNode,
        channelId: propagate.inChannel,
        remainingTicks: propagate.afterTicks,
        interceptableBy: propagate.interceptableBy ?? [],
    };
    draft.inFlight = [...draft.inFlight, hop];
    effects.push({ type: 'PROPOSAL_IN_FLIGHT', hop });
}
/**
 * Applies one outcome to the state, in the fixed effect order.
 *
 * Returns the new state and the effects, in the order they happened. Nothing
 * here reads a clock or a random number: an outcome is entirely determined by
 * what it declares.
 */
export function applyOutcome(state, outcome, context) {
    return applyOutcomeAt(state, outcome, context, 0);
}
/**
 * Outcomes an outcome causes: a puppet's `unlockOutcome` when trust tips it
 * over, and a `pipLadder` rung's `outcome` when a strike reaches it. They are
 * applied after the outcome that triggered them has finished, so HR's reply
 * lands after the thing it is replying to rather than in the middle of it.
 *
 * Depth is capped rather than trusted: a follow-up that unlocks a puppet whose
 * own unlock outcome unlocks another is authorable, and a cycle is a content
 * bug that must not become a stack overflow in the solver's inner loop.
 */
const MAX_FOLLOW_UP_DEPTH = 3;
function applyOutcomeAt(state, outcome, context, depth) {
    const draft = draftOf(state);
    const effects = [];
    const loaded = context.loaded;
    const followUps = [];
    // 1. State deltas.
    for (const delta of outcome.stateDeltas ?? []) {
        if (delta.nodeId === 'player' && delta.var === 'cred') {
            draft.cred = delta.op === 'set' ? delta.value : draft.cred + delta.value;
            effects.push({
                type: 'NODE_STATE_CHANGED',
                nodeId: 'player',
                var: 'cred',
                value: draft.cred,
            });
            continue;
        }
        const next = delta.op === 'set' ? delta.value : nodeVar(draft, delta.nodeId, delta.var) + delta.value;
        setNodeVar(draft, delta.nodeId, delta.var, next);
        effects.push({
            type: 'NODE_STATE_CHANGED',
            nodeId: delta.nodeId,
            var: delta.var,
            value: next,
        });
    }
    for (const nodeId of refreshPuppetUnlocks(draft, loaded, effects)) {
        const celebration = loaded.puppets[nodeId]?.unlockOutcome;
        if (celebration !== undefined)
            followUps.push(celebration);
    }
    // 2. Suspicion, scaled by the difficulty profile.
    for (const delta of outcome.suspicionDeltas ?? []) {
        raiseSuspicion(draft, loaded, delta.nodeId, delta.delta, effects);
    }
    // 3. HR strikes.
    if (outcome.pipDelta !== undefined && outcome.pipDelta !== 0) {
        const rungOutcome = applyStrikes(draft, loaded, outcome.pipDelta, effects);
        if (rungOutcome !== null)
            followUps.push(rungOutcome);
    }
    // 4. Proposal mutations — how this node rewrites the pitch before passing it on.
    const mutations = context.canMutateProposal === false ? [] : (outcome.proposalMutations ?? []);
    if (mutations.length > 0) {
        let tags = draft.proposal.tags;
        let attribution = draft.proposal.attribution;
        for (const mutation of mutations) {
            // `Bcc Shield`: somebody senior is already on the thread, so the credit
            // cannot be quietly moved. Suppressed rather than compensated, because
            // `proposal: { attribution }` rules downstream then read the truth.
            if (mutation.op === 'setAttribution' &&
                draft.settings.blocksAttributionMutation &&
                attribution === 'player' &&
                mutation.value !== 'player') {
                continue;
            }
            switch (mutation.op) {
                case 'addTag':
                    if (mutation.axis !== undefined && mutation.value !== undefined) {
                        tags = addTag(tags, mutation.axis, mutation.value);
                    }
                    break;
                case 'removeTag':
                    if (mutation.axis !== undefined && mutation.value !== undefined) {
                        tags = removeTag(tags, mutation.axis, mutation.value);
                    }
                    break;
                case 'clearAxis':
                    if (mutation.axis !== undefined)
                        tags = clearAxis(tags, mutation.axis);
                    break;
                case 'setAttribution':
                    if (mutation.value !== undefined)
                        attribution = mutation.value;
                    break;
            }
        }
        draft.proposal = { ...draft.proposal, tags, attribution };
        effects.push({ type: 'PROPOSAL_MUTATED', tags, attribution });
    }
    // 5. Flags.
    const setFlags = outcome.flags?.set ?? [];
    const clearedFlags = outcome.flags?.clear ?? [];
    if (setFlags.length > 0 || clearedFlags.length > 0) {
        draft.flags = sortedUnique(removeAll(addSorted(draft.flags, setFlags), clearedFlags));
        effects.push({ type: 'FLAGS_CHANGED', set: setFlags, cleared: clearedFlags });
    }
    // 6. Grants.
    const grants = outcome.grants;
    if (grants !== undefined) {
        const particles = grants.particles ?? [];
        if (particles.length > 0) {
            draft.unlocked = {
                ...draft.unlocked,
                particles: addSorted(draft.unlocked.particles, particles),
            };
            effects.push({ type: 'PARTICLES_GRANTED', particleIds: particles });
        }
        const channels = grants.channels ?? [];
        if (channels.length > 0) {
            const updated = { ...draft.channels };
            for (const channelId of channels) {
                const existing = updated[channelId];
                if (existing !== undefined)
                    updated[channelId] = { ...existing, unlocked: true };
            }
            draft.channels = updated;
            draft.unlocked = {
                ...draft.unlocked,
                channels: addSorted(draft.unlocked.channels, channels),
            };
            effects.push({ type: 'CHANNELS_GRANTED', channelIds: channels });
        }
        for (const nodeId of grants.puppets ?? []) {
            const existing = draft.puppets[nodeId];
            if (existing === undefined || existing.unlocked)
                continue;
            draft.puppets = { ...draft.puppets, [nodeId]: { ...existing, unlocked: true } };
            draft.unlocked = {
                ...draft.unlocked,
                puppets: addSorted(draft.unlocked.puppets, [nodeId]),
            };
            effects.push({ type: 'PUPPET_UNLOCKED', nodeId });
            const celebration = loaded.puppets[nodeId]?.unlockOutcome;
            if (celebration !== undefined)
                followUps.push(celebration);
        }
    }
    // 7. Posts — enqueued against their delay, never applied here.
    const origin = context.source === 'scheduled' ? 'system' : 'node';
    for (const post of outcome.post ?? []) {
        draft.pendingPosts = [
            ...draft.pendingPosts,
            {
                id: `post-${String(draft.messageOrdinal)}-${String(draft.pendingPosts.length)}`,
                remainingTicks: post.afterTicks ?? 0,
                post,
                origin,
            },
        ];
    }
    // 8. Propagation — likewise enqueued, and the interception window with it.
    applyPropagation(draft, loaded, outcome, effects);
    // 9. End of level.
    if (outcome.endsLevel !== undefined && draft.end === null) {
        draft.end = {
            result: outcome.endsLevel.result,
            reason: outcome.endsLevel.reason,
            summary: outcome.endsLevel.summary ?? null,
            partialCredit: outcome.endsLevel.partialCredit ?? null,
        };
        effects.push({ type: 'LEVEL_ENDED', end: draft.end });
    }
    // The authored sentiment reaction lands on the player's own message — the
    // 😬 that tells you how it was received before anyone replies.
    if (outcome.reaction !== undefined && context.reactToMessageId !== null) {
        const targetId = context.reactToMessageId;
        draft.messages = draft.messages.map((message) => message.id === targetId
            ? { ...message, reactions: [...message.reactions, outcome.reaction] }
            : message);
    }
    // 10. Whatever this outcome set in motion — the unlock celebration, HR's
    //     note about the strike — after everything it did itself.
    if (followUps.length === 0 || depth >= MAX_FOLLOW_UP_DEPTH) {
        return { state: asState(draft), effects };
    }
    let current = asState(draft);
    for (const outcomeId of followUps) {
        const followUp = loaded.outcomes[outcomeId];
        if (followUp === undefined)
            continue;
        const applied = applyOutcomeAt(current, followUp, context, depth + 1);
        current = applied.state;
        effects.push(...applied.effects);
    }
    return { state: current, effects };
}
//# sourceMappingURL=outcomes.js.map